import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Toggle from '../Toggle/Toggle';
import { resolveImageUrl } from '../../utils/imageUrl';
import useNavigationPrompt from '../../hooks/useNavigationPrompt';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Category, BranchDto, ServiceDetailsResponse, AgeGroup, ServiceOptionGroupDto } from '../../types/api-responses';
import { generateUniqueServiceName } from './generateUniqueServiceName';
import styles from './ServiceAddEditModal.module.css';
// PBI 1962 (AB#1968) — flexible option-groups builder (flag-gated, default OFF).
import ServiceOptionGroupsBuilder from '../ServiceOptionGroupsBuilder/ServiceOptionGroupsBuilder';
import { isServiceOptionGroupsEnabled } from '../../lib/serviceOptionGroups/serviceOptionGroupsFlag';
import type { OptionGroupModel, OptionGroupWirePayload } from '../../types/serviceOptionGroups';
import {
    dtoToModel,
    durationStringToMinutes,
    makeDefaultVariantGroup,
    modelToWirePayload,
    modelToFlatPricingOptions,
    validateOptionGroups,
} from '../../lib/serviceOptionGroups/serviceOptionGroups';

/**
 * CR-2026-05-12-v2 — Service add/edit modal (canonical single-modal pattern).
 *
 * Replaces the standalone /services 3-step wizard for the My Business shell
 * entry point. Single page, dark Fresha tokens, RHF + Zod, no staff
 * assignment (deferred to a follow-up PBI). Existing assignedStaffIds are
 * threaded through unchanged on save so dropping the staff step here doesn't
 * wipe assignments set previously via the standalone wizard.
 *
 * Currency is hardcoded to AED for v1 — currency-per-business inheritance
 * arrives with the marketplace pricing PBI.
 */

const SERVICE_TYPE = {
    MEN: 1,
    WOMEN: 2,
    EVERYONE: 3,
} as const;

const DURATION_OPTIONS: Array<{ value: string; label: string }> = [
    { value: '00:15:00', label: '15 min' },
    { value: '00:30:00', label: '30 min' },
    { value: '00:45:00', label: '45 min' },
    { value: '01:00:00', label: '1 hr' },
    { value: '01:15:00', label: '1 hr 15 min' },
    { value: '01:30:00', label: '1 hr 30 min' },
    { value: '02:00:00', label: '2 hr' },
    { value: '02:30:00', label: '2 hr 30 min' },
    { value: '03:00:00', label: '3 hr' },
];

const DEFAULT_CURRENCY = 'AED';
const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5 MB

/* ── Pricing tier sub-schema ──────────────────────────────────────── */
const pricingOptionSchema = z.object({
    name: z
        .string()
        .min(1, 'Tier name is required')
        .max(100, 'Tier name cannot exceed 100 characters'),
    price: z.number({ invalid_type_error: 'Price is required' }).min(0, 'Price must be ≥ 0'),
    currency: z.string().min(1).max(3),
    duration: z.string().min(1, 'Duration is required'),
});

/* ── Modal form schema (image handled outside RHF) ────────────────── */
const formSchema = z.object({
    name: z
        .string()
        .min(2, 'Service name must be at least 2 characters')
        .max(100, 'Service name cannot exceed 100 characters'),
    branchId: z.number({ invalid_type_error: 'Branch is required' }).positive('Branch is required'),
    categoryId: z.number().min(0),
    minDuration: z.string().min(1, 'Duration is required'),
    maxDuration: z.string().min(1, 'Duration is required'),
    serviceType: z.number().int().min(1).max(3),
    // AB#1847 (PBI-I) / AB#1838 — Age axis of the gender matrix. REQUIRED;
    // the owner must explicitly pick Adult / Kid / Both (no implicit default
    // in the form). The form stores `'Adult' | 'Kid' | 'Both' | ''` so the
    // empty-string sentinel maps to "unchosen" — refine rejects it with the
    // same field-error UX as the other required pickers.
    ageGroup: z
        .union([z.literal(''), z.literal('Adult'), z.literal('Kid'), z.literal('Both')])
        .refine(v => v === 'Adult' || v === 'Kid' || v === 'Both', {
            message: 'Please pick who this service is for',
        }),
    hasHomeService: z.boolean(),
    isActive: z.boolean(),
    description: z
        .string()
        .max(1000, 'Description cannot exceed 1000 characters')
        .optional()
        .or(z.literal('')),
    pricingOptions: z
        .array(pricingOptionSchema)
        .min(1, 'At least one pricing tier is required'),
});

// AB#1847 — Use `z.input` (pre-refine) so the form value type for ageGroup
// includes the empty-string "unchosen" sentinel. After validation succeeds
// the value is narrowed to `'Adult' | 'Kid' | 'Both'` in `onValid`.
type ServiceForm = z.input<typeof formSchema>;

/** External-facing data shape — parent constructs this and reads it back on save. */
export interface ServiceFormData {
    categoryId: number;
    branchId: number;
    name: string;
    image: File | null;
    removeExistingImage: boolean;
    minDuration: string;
    maxDuration: string;
    serviceType: number;
    /**
     * AB#1847 (PBI-I) / AB#1838 — Age axis of the gender matrix. Required
     * on create — the parent factory (`emptyModalData`) initialises this
     * to `null` so the modal forces an explicit pick before submit. On
     * edit (`rowToModalData`) the BE row is back-filled to "Adult" if
     * missing, so this is always non-null at render time.
     */
    ageGroup: AgeGroup | null;
    hasHomeService: boolean;
    description: string;
    isActive: boolean;
    pricingOptions: Array<{
        name: string;
        price: number;
        currency: string;
        duration: string;
    }>;
    /**
     * PBI 1962 (AB#1968) — flexible option groups (variants & add-ons), as the
     * serialized `OptionGroupsJson` wire payload. Populated by the modal ONLY
     * when the option-groups builder flag is ON; otherwise omitted so the
     * backend auto-wraps the flat `pricingOptions` (exact legacy path). The
     * parent forwards it verbatim to createService/updateService.
     */
    optionGroups?: OptionGroupWirePayload[];
    /**
     * PBI 1962 (AB#1968) — existing option-group DTOs read from the service
     * (edit mode) used to SEED the builder. Read-only input; not sent back
     * (the modal sends the serialized `optionGroups` above instead).
     */
    existingOptionGroups?: ServiceOptionGroupDto[];
    /** Pre-existing staff assignments, threaded through untouched on save. */
    preservedStaffIds: number[];
}

export interface ServiceAddEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: ServiceFormData) => void | Promise<void>;
    initialData: ServiceFormData;
    isAdd: boolean;
    loading?: boolean;
    onDirtyChange?: (dirty: boolean) => void;
    onDeleteClicked?: () => void;
    categories: Category[];
    branches: BranchDto[];
    existingImageUrl?: string;
    /**
     * γ-lite (B6') — every existing service across the business. When set
     * and the modal is in Add mode, a "Copy from another service" picker
     * appears at the top of the form. Picking pre-fills name, durations,
     * service type, home-service flag, description, pricing tiers, and
     * category — but NOT branch (the owner still chooses the target
     * branch) and NOT the image (each branch typically wants its own
     * imagery, and File objects don't survive a copy operation cleanly).
     *
     * The new row that gets saved is fully independent — editing it later
     * never affects the original. This is a deliberate UX time-saver
     * without taking a stance on cross-branch templating (see γ-full
     * discussion in audit doc / chat).
     */
    existingServices?: ServiceDetailsResponse[];
}

const ServiceAddEditModal: React.FC<ServiceAddEditModalProps> = ({
    isOpen,
    onClose,
    onSave,
    initialData,
    isAdd,
    loading = false,
    onDirtyChange,
    onDeleteClicked,
    categories,
    branches,
    existingImageUrl,
    existingServices,
}) => {
    const { t } = useLanguage();

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors, isDirty },
    } = useForm<ServiceForm>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: initialData.name,
            branchId: initialData.branchId || 0,
            categoryId: initialData.categoryId || 0,
            minDuration: initialData.minDuration,
            maxDuration: initialData.maxDuration,
            serviceType: initialData.serviceType,
            // AB#1847 (PBI-I) — empty sentinel = "unchosen". On Add the
            // factory passes null → '' here; on Edit the BE row populates
            // 'Adult' / 'Kid' / 'Both' directly.
            ageGroup: initialData.ageGroup ?? '',
            hasHomeService: initialData.hasHomeService,
            isActive: initialData.isActive,
            description: initialData.description,
            pricingOptions: initialData.pricingOptions,
        },
    });

    // Image is managed outside RHF (File objects don't serialize cleanly).
    const [image, setImage] = useState<File | null>(null);
    const [removeExistingImage, setRemoveExistingImage] = useState(false);
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
    const [imageError, setImageError] = useState<string | null>(null);

    /* ── PBI 1962 (AB#1968) — option-groups builder (flag-gated) ─────── */
    // Feature flag defaults OFF → the classic flat pricing-tiers UI renders and
    // only PricingOptionsJson is sent (ships DARK). When ON, the builder
    // replaces the flat list, is seeded from existing groups (or the flat
    // options), managed outside RHF, and serialized on submit.
    const optionGroupsEnabled = useMemo(() => isServiceOptionGroupsEnabled(), []);
    const [optionGroups, setOptionGroups] = useState<OptionGroupModel[]>([]);
    const [optionGroupsTouched, setOptionGroupsTouched] = useState(false);

    const watchedPricing = watch('pricingOptions');
    const watchedMin = watch('minDuration');
    const watchedMax = watch('maxDuration');

    /* ── Image preview lifecycle ────────────────────────────────────── */
    useEffect(() => {
        if (!image) {
            setImagePreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(image);
        setImagePreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [image]);

    /* ── Reset form when modal opens with fresh initialData ─────────── */
    useEffect(() => {
        if (!isOpen) return;
        reset({
            name: initialData.name,
            branchId: initialData.branchId || 0,
            categoryId: initialData.categoryId || 0,
            minDuration: initialData.minDuration,
            maxDuration: initialData.maxDuration,
            serviceType: initialData.serviceType,
            ageGroup: initialData.ageGroup ?? '',
            hasHomeService: initialData.hasHomeService,
            isActive: initialData.isActive,
            description: initialData.description,
            pricingOptions: initialData.pricingOptions,
        });
        setImage(null);
        setRemoveExistingImage(false);
        setImageError(null);
        // PBI 1962 — seed the option-groups builder. Existing groups (edit mode)
        // map via dtoToModel and carry the legacy auto-wrap badge; otherwise we
        // wrap the flat pricing options into a default Single/1..1 Variant group
        // so legacy + net-new services start uniform (Faris §10.C).
        if (optionGroupsEnabled) {
            const existing = initialData.existingOptionGroups;
            if (existing && existing.length > 0) {
                setOptionGroups(dtoToModel(existing));
            } else {
                setOptionGroups([
                    makeDefaultVariantGroup(
                        initialData.pricingOptions.map(p => ({
                            name: p.name,
                            price: p.price,
                            durationMin: durationStringToMinutes(p.duration),
                        })),
                    ),
                ]);
            }
        } else {
            setOptionGroups([]);
        }
        setOptionGroupsTouched(false);
        if (onDirtyChange) onDirtyChange(false);
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── Notify parent of dirty state ───────────────────────────────── */
    useEffect(() => {
        if (!onDirtyChange) return;
        const imageChanged = image !== null || removeExistingImage;
        onDirtyChange(isDirty || imageChanged);
    }, [isDirty, image, removeExistingImage, onDirtyChange]);

    /* ── PBI 1962 — validate option groups (mirrors backend rules) ──── */
    const optionGroupsValidation = useMemo(
        () => validateOptionGroups(optionGroups),
        [optionGroups],
    );

    const handleOptionGroupsChange = useCallback((next: OptionGroupModel[]) => {
        setOptionGroups(next);
        setOptionGroupsTouched(true);
        if (onDirtyChange) onDirtyChange(true);
    }, [onDirtyChange]);

    /* ── Validate min ≤ max ─────────────────────────────────────────── */
    const durationConflict = useMemo(() => {
        const minIdx = DURATION_OPTIONS.findIndex(o => o.value === watchedMin);
        const maxIdx = DURATION_OPTIONS.findIndex(o => o.value === watchedMax);
        if (minIdx < 0 || maxIdx < 0) return null;
        if (minIdx > maxIdx) return 'Min duration cannot exceed max duration';
        return null;
    }, [watchedMin, watchedMax]);

    /* ── Image handlers ─────────────────────────────────────────────── */
    const handleImagePick = (file: File | null) => {
        setImageError(null);
        if (!file) {
            setImage(null);
            return;
        }
        if (!file.type.startsWith('image/')) {
            setImageError('Please choose an image file.');
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            setImageError('Image must be 5 MB or smaller.');
            return;
        }
        setImage(file);
        setRemoveExistingImage(false);
    };

    const handleRemoveImage = () => {
        setImage(null);
        setRemoveExistingImage(true);
        setImageError(null);
    };

    /* ── Pricing tier helpers ───────────────────────────────────────── */
    const addPricingTier = () => {
        setValue(
            'pricingOptions',
            [
                ...watchedPricing,
                { name: '', price: 0, currency: DEFAULT_CURRENCY, duration: watchedMin },
            ],
            { shouldDirty: true },
        );
    };

    const removePricingTier = (index: number) => {
        if (watchedPricing.length <= 1) return;
        setValue(
            'pricingOptions',
            watchedPricing.filter((_, i) => i !== index),
            { shouldDirty: true },
        );
    };

    /* ── Submit ─────────────────────────────────────────────────────── */
    const onValid = (data: ServiceForm) => {
        if (durationConflict) return;
        // PBI 1962 — when the builder is ON, block save on invalid groups and
        // surface the error banner (backend re-validates regardless).
        if (optionGroupsEnabled && !optionGroupsValidation.ok) {
            setOptionGroupsTouched(true);
            return;
        }
        // AB#1847 — `ageGroup` passes Zod refine only as the closed enum,
        // so this narrow is safe at this point. The cast is a documentation
        // assert rather than a behavioural change.
        const ageGroup = data.ageGroup as AgeGroup;

        // PBI 1962 — when ON, the builder is authoritative: serialize
        // OptionGroupsJson AND derive the flat PricingOptionsJson fallback from
        // the Variant group (dual-write — keeps old readers + the Appointment
        // scalar FK satisfied). When OFF, send the untouched flat options only.
        const currency = data.pricingOptions[0]?.currency ?? DEFAULT_CURRENCY;
        const optionGroupsPayload: OptionGroupWirePayload[] | undefined =
            optionGroupsEnabled ? modelToWirePayload(optionGroups, currency) : undefined;
        const pricingOptions =
            optionGroupsEnabled
                ? modelToFlatPricingOptions(optionGroups, currency)
                : data.pricingOptions;

        const result: ServiceFormData = {
            // B16 — In Edit mode, BE now accepts CategoryId and re-parents
            // when it differs from the current. Always pass data.categoryId
            // so the owner can move a mis-categorized service in-place.
            categoryId: data.categoryId,
            branchId: data.branchId,
            name: data.name.trim(),
            image,
            removeExistingImage,
            minDuration: data.minDuration,
            maxDuration: data.maxDuration,
            serviceType: data.serviceType,
            ageGroup,
            hasHomeService: data.hasHomeService,
            description: data.description ?? '',
            isActive: data.isActive,
            pricingOptions,
            optionGroups: optionGroupsPayload,
            preservedStaffIds: initialData.preservedStaffIds ?? [],
        };
        onSave(result);
    };

    const handleClose = useCallback(() => {
        const imageChanged = image !== null || removeExistingImage;
        if (isDirty || imageChanged) {
            const ok = window.confirm('You have unsaved changes. Discard them?');
            if (!ok) return;
        }
        onClose();
    }, [isDirty, image, removeExistingImage, onClose]);

    useNavigationPrompt(
        isOpen && (isDirty || image !== null || removeExistingImage),
        'You have unsaved changes. Do you want to discard them?',
    );

    /* ── Render ─────────────────────────────────────────────────────── */
    return (
        <div className={`${styles.modalBackground} ${isOpen ? styles.open : ''}`}>
            <div className={`${styles.modalContainer} ${isOpen ? styles.open : ''}`}>
                {loading && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.loadingContent}>
                            <div className={styles.spinner} />
                            <span>{isAdd ? 'Creating service…' : 'Saving service…'}</span>
                        </div>
                    </div>
                )}

                <div className={styles.headerCloseInputsContainer}>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={handleClose}
                        disabled={loading}
                        aria-label="Close"
                    >
                        ×
                    </button>
                    <div>
                        <h2 className={styles.header}>
                            {isAdd ? 'Add service' : 'Edit service'}
                        </h2>
                        <p className={styles.subheader}>
                            What you offer, how long it takes, and what it costs. Customers see
                            this on your marketplace listing and booking page.
                        </p>
                    </div>

                    {/* γ-lite — "Copy from another service" picker.
                        Only in Add mode + when there's at least one existing
                        service in the business. Picking copies name + durations
                        + service type + home-service flag + description +
                        pricing tiers + category into the form below, then the
                        owner edits whatever needs to differ for this branch.
                        The new row is saved as fully independent — editing
                        it later never affects the original. */}
                    {isAdd && existingServices && existingServices.length > 0 && (
                        <div className={styles.copyFromSection}>
                            <label htmlFor="service-copy-from" className={styles.sectionLabel}>
                                Copy from another service <span className={styles.optionalHint}>(saves typing)</span>
                            </label>
                            <select
                                id="service-copy-from"
                                className={styles.copyFromSelect}
                                defaultValue=""
                                disabled={loading}
                                onChange={(e) => {
                                    const sourceId = parseInt(e.target.value, 10);
                                    if (!sourceId) return;
                                    const src = existingServices.find(s => s.id === sourceId);
                                    if (!src) return;
                                    // AB#1899 — Auto-suffix the name with " (copy)" to
                                    // avoid the BE per-branch + per-category name-
                                    // uniqueness 400. Collisions in the same scope
                                    // (source category + source branch) escalate to
                                    // (copy 2), (copy 3), etc. The owner can still
                                    // edit the suggestion freely.
                                    const sourceCategoryName = src.categoryName;
                                    const sourceBranchId = src.branchId;
                                    const existingNamesInScope = existingServices
                                        .filter(s => s.categoryName === sourceCategoryName
                                            && s.branchId === sourceBranchId)
                                        .map(s => s.name);
                                    const suggestedName = generateUniqueServiceName(
                                        src.name,
                                        existingNamesInScope,
                                        t('services.copyFrom.suffix.first'),
                                        (n: number) => t('services.copyFrom.suffix.nth')
                                            .replace('{n}', String(n)),
                                    );
                                    // Pre-fill RHF fields. Branch is left untouched —
                                    // owner chooses where to add this copy. Image is
                                    // never copied (File objects don't transfer; each
                                    // branch typically wants its own imagery).
                                    setValue('name', suggestedName, { shouldDirty: true });
                                    setValue('minDuration', src.minDuration, { shouldDirty: true });
                                    setValue('maxDuration', src.maxDuration, { shouldDirty: true });
                                    setValue('serviceType', typeof src.serviceType === 'number'
                                        ? src.serviceType
                                        : src.serviceType === 'MenOnly' ? 1
                                            : src.serviceType === 'WomenOnly' ? 2
                                                : 3,
                                        { shouldDirty: true });
                                    setValue('hasHomeService', !!src.hasHomeService, { shouldDirty: true });
                                    setValue('description', src.description ?? '', { shouldDirty: true });
                                    // B16 — BE now accepts CategoryId on edit too,
                                    // so this lookup works in both Add and Edit modes.
                                    // Find by name (most reliable cross-branch lookup
                                    // since IDs are business-scoped).
                                    const sourceCat = categories.find(c => c.name === src.categoryName);
                                    if (sourceCat) {
                                        setValue('categoryId', sourceCat.id, { shouldDirty: true });
                                    }
                                    if (src.pricingOptions && src.pricingOptions.length > 0) {
                                        setValue('pricingOptions', src.pricingOptions.map(p => ({
                                            name: p.name,
                                            price: p.price,
                                            currency: p.currency,
                                            duration: p.duration,
                                        })), { shouldDirty: true });
                                    }
                                    // Reset the picker so the same selection can be
                                    // re-applied (rare, but possible if the owner
                                    // edited fields and wants to start fresh).
                                    e.target.value = '';
                                }}
                            >
                                <option value="">— Start blank, or pick to copy —</option>
                                {existingServices.map(svc => {
                                    const branchName = branches.find(b => b.id === svc.branchId)?.name ?? 'Unknown branch';
                                    return (
                                        <option key={svc.id} value={svc.id}>
                                            {svc.name} · {branchName}
                                        </option>
                                    );
                                })}
                            </select>
                            <p className={styles.copyFromHint}>
                                Copies the name, duration, service type, description, and pricing tiers from another service in your salon. You can then adjust anything for this branch. The original is not affected.
                            </p>
                        </div>
                    )}

                    {/* Image uploader */}
                    <div>
                        <label className={styles.sectionLabel}>Service photo (optional)</label>
                        <div className={styles.imageRow}>
                            <label className={styles.imageDrop}>
                                {imagePreviewUrl ? (
                                    <img src={imagePreviewUrl} alt="" />
                                ) : !removeExistingImage && existingImageUrl ? (
                                    <img src={resolveImageUrl(existingImageUrl)} alt="" />
                                ) : (
                                    <>
                                        <span className={styles.imageDropIcon} aria-hidden="true">📷</span>
                                        <span>Upload</span>
                                    </>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={e => handleImagePick(e.target.files?.[0] ?? null)}
                                    disabled={loading}
                                />
                            </label>
                            <div className={styles.imageActions}>
                                <span className={styles.imageHint}>
                                    PNG or JPG, up to 5 MB. Squareish photos look best.
                                </span>
                                {(image ||
                                    (!removeExistingImage && existingImageUrl)) && (
                                    <button
                                        type="button"
                                        className={styles.removeImageBtn}
                                        onClick={handleRemoveImage}
                                        disabled={loading}
                                    >
                                        Remove image
                                    </button>
                                )}
                                {imageError && (
                                    <div className={styles.fieldError}>{imageError}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Service name */}
                    <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                            <div className={styles.field}>
                                <label htmlFor="service-name">Service name</label>
                                <input
                                    id="service-name"
                                    type="text"
                                    placeholder="e.g. Hair color & blow-dry"
                                    value={field.value}
                                    onChange={e => field.onChange(e.target.value)}
                                    disabled={loading}
                                    maxLength={100}
                                />
                                {errors.name && (
                                    <div className={styles.fieldError}>{errors.name.message}</div>
                                )}
                            </div>
                        )}
                    />

                    {/* B16 — Category dropdown rendered in BOTH Add and Edit
                        modes. BE's UpdateServiceCommand now accepts an optional
                        CategoryId and re-parents the service when it differs
                        from the current one. Owners can fix a mis-categorized
                        service in-place without delete + re-add. */}
                    <Controller
                        name="categoryId"
                        control={control}
                        render={({ field }) => (
                            <div className={styles.field}>
                                <label htmlFor="service-category">Category</label>
                                <select
                                    id="service-category"
                                    value={field.value || ''}
                                    onChange={e =>
                                        field.onChange(parseInt(e.target.value, 10) || 0)
                                    }
                                    disabled={loading}
                                >
                                    <option value="">— Select a category —</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>
                                            {cat.name}
                                        </option>
                                    ))}
                                </select>
                                {field.value === 0 && errors.categoryId && (
                                    <div className={styles.fieldError}>Category is required</div>
                                )}
                            </div>
                        )}
                    />


                    {/* Branch */}
                    <Controller
                        name="branchId"
                        control={control}
                        render={({ field }) => (
                            <div className={styles.field}>
                                <label htmlFor="service-branch">Branch</label>
                                <select
                                    id="service-branch"
                                    value={field.value || ''}
                                    onChange={e =>
                                        field.onChange(parseInt(e.target.value, 10) || 0)
                                    }
                                    disabled={loading}
                                >
                                    <option value="">— Select a branch —</option>
                                    {branches.map(b => (
                                        <option key={b.id} value={b.id}>
                                            {b.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.branchId && (
                                    <div className={styles.fieldError}>{errors.branchId.message}</div>
                                )}
                            </div>
                        )}
                    />

                    {/* Service type — chip-button group */}
                    <Controller
                        name="serviceType"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <label className={styles.sectionLabel}>Service type</label>
                                <div className={styles.chipGroup}>
                                    <button
                                        type="button"
                                        className={`${styles.chip} ${field.value === SERVICE_TYPE.EVERYONE ? styles.chipActive : ''}`}
                                        onClick={() => field.onChange(SERVICE_TYPE.EVERYONE)}
                                        disabled={loading}
                                    >
                                        Everyone
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.chip} ${field.value === SERVICE_TYPE.WOMEN ? styles.chipActive : ''}`}
                                        onClick={() => field.onChange(SERVICE_TYPE.WOMEN)}
                                        disabled={loading}
                                    >
                                        Women only
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.chip} ${field.value === SERVICE_TYPE.MEN ? styles.chipActive : ''}`}
                                        onClick={() => field.onChange(SERVICE_TYPE.MEN)}
                                        disabled={loading}
                                    >
                                        Men only
                                    </button>
                                </div>
                            </div>
                        )}
                    />

                    {/* AB#1847 (PBI-I) / AB#1838 — Age-group axis of the
                        gender matrix. REQUIRED on Add/Edit; the owner must
                        explicitly pick Adult / Kid / Both. AR `Both` =
                        `كلاهما` per founder Q1 (NOT `الجميع`, which is the
                        gender-axis `Everyone` label). Bilingual via t(). */}
                    <Controller
                        name="ageGroup"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <label className={styles.sectionLabel}>
                                    {t('services.ageGroup.label')}
                                </label>
                                <div
                                    className={styles.chipGroup}
                                    role="radiogroup"
                                    aria-label={t('services.ageGroup.label')}
                                    aria-required="true"
                                >
                                    <button
                                        type="button"
                                        className={`${styles.chip} ${field.value === 'Adult' ? styles.chipActive : ''}`}
                                        onClick={() => field.onChange('Adult')}
                                        disabled={loading}
                                        aria-checked={field.value === 'Adult'}
                                        role="radio"
                                    >
                                        {t('services.ageGroup.adults')}
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.chip} ${field.value === 'Kid' ? styles.chipActive : ''}`}
                                        onClick={() => field.onChange('Kid')}
                                        disabled={loading}
                                        aria-checked={field.value === 'Kid'}
                                        role="radio"
                                    >
                                        {t('services.ageGroup.kids')}
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.chip} ${field.value === 'Both' ? styles.chipActive : ''}`}
                                        onClick={() => field.onChange('Both')}
                                        disabled={loading}
                                        aria-checked={field.value === 'Both'}
                                        role="radio"
                                    >
                                        {t('services.ageGroup.both')}
                                    </button>
                                </div>
                                {errors.ageGroup && (
                                    <div className={styles.fieldError} role="alert">
                                        {t('services.ageGroup.required')}
                                    </div>
                                )}
                            </div>
                        )}
                    />

                    {/* Min / Max duration */}
                    <div className={styles.row}>
                        <Controller
                            name="minDuration"
                            control={control}
                            render={({ field }) => (
                                <div className={styles.field}>
                                    <label htmlFor="service-min-duration">Minimum duration</label>
                                    <select
                                        id="service-min-duration"
                                        value={field.value}
                                        onChange={e => field.onChange(e.target.value)}
                                        disabled={loading}
                                    >
                                        {DURATION_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        />
                        <Controller
                            name="maxDuration"
                            control={control}
                            render={({ field }) => (
                                <div className={styles.field}>
                                    <label htmlFor="service-max-duration">Maximum duration</label>
                                    <select
                                        id="service-max-duration"
                                        value={field.value}
                                        onChange={e => field.onChange(e.target.value)}
                                        disabled={loading}
                                    >
                                        {DURATION_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        />
                    </div>
                    {durationConflict && (
                        <div className={styles.fieldError}>{durationConflict}</div>
                    )}

                    {/* PBI 1962 (AB#1968) — option-groups builder (flag ON) OR
                        the legacy flat pricing-tiers list (flag OFF, default). */}
                    {optionGroupsEnabled ? (
                        <div>
                            <label className={styles.sectionLabel}>
                                {t('services.builder.title')}
                            </label>
                            <ServiceOptionGroupsBuilder
                                value={optionGroups}
                                onChange={handleOptionGroupsChange}
                                currency={watchedPricing[0]?.currency ?? DEFAULT_CURRENCY}
                                disabled={loading}
                                errorKeys={
                                    optionGroupsTouched ? optionGroupsValidation.errorKeys : []
                                }
                            />
                        </div>
                    ) : (
                    /* Pricing tiers */
                    <div>
                        <label className={styles.sectionLabel}>
                            Pricing tiers ({watchedPricing.length})
                        </label>
                        <div className={styles.pricingList}>
                            {watchedPricing.map((tier, index) => (
                                <div key={index} className={styles.pricingRow}>
                                    <div className={styles.pricingRowHeader}>
                                        <span className={styles.pricingRowLabel}>
                                            Tier {index + 1}
                                        </span>
                                        {watchedPricing.length > 1 && (
                                            <button
                                                type="button"
                                                className={styles.removeTierBtn}
                                                onClick={() => removePricingTier(index)}
                                                disabled={loading}
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                    <div className={styles.pricingRowGrid}>
                                        <Controller
                                            name={`pricingOptions.${index}.name` as const}
                                            control={control}
                                            render={({ field }) => (
                                                <div className={styles.field}>
                                                    <label>Tier name</label>
                                                    <input
                                                        type="text"
                                                        placeholder="e.g. Short hair"
                                                        value={field.value}
                                                        onChange={e => field.onChange(e.target.value)}
                                                        disabled={loading}
                                                        maxLength={100}
                                                    />
                                                    {errors.pricingOptions?.[index]?.name && (
                                                        <div className={styles.fieldError}>
                                                            {errors.pricingOptions[index]?.name?.message}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        />
                                        <Controller
                                            name={`pricingOptions.${index}.duration` as const}
                                            control={control}
                                            render={({ field }) => (
                                                <div className={styles.field}>
                                                    <label>Duration</label>
                                                    <select
                                                        value={field.value}
                                                        onChange={e => field.onChange(e.target.value)}
                                                        disabled={loading}
                                                    >
                                                        {DURATION_OPTIONS.map(opt => (
                                                            <option key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                        />
                                        <Controller
                                            name={`pricingOptions.${index}.price` as const}
                                            control={control}
                                            render={({ field }) => (
                                                <div className={styles.field}>
                                                    <label>Price</label>
                                                    <div className={styles.priceInputWrap}>
                                                        <input
                                                            type="number"
                                                            inputMode="decimal"
                                                            min="0"
                                                            step="0.01"
                                                            placeholder="0"
                                                            value={field.value || ''}
                                                            onChange={e => {
                                                                const v = parseFloat(e.target.value);
                                                                field.onChange(isNaN(v) ? 0 : v);
                                                            }}
                                                            disabled={loading}
                                                        />
                                                        <span className={styles.priceCurrency}>
                                                            {watchedPricing[index]?.currency ?? DEFAULT_CURRENCY}
                                                        </span>
                                                    </div>
                                                    {errors.pricingOptions?.[index]?.price && (
                                                        <div className={styles.fieldError}>
                                                            {errors.pricingOptions[index]?.price?.message}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            className={styles.addTierBtn}
                            onClick={addPricingTier}
                            disabled={loading}
                            style={{ marginTop: 10 }}
                        >
                            + Add another tier
                        </button>
                        {errors.pricingOptions && typeof errors.pricingOptions.message === 'string' && (
                            <div className={styles.fieldError} style={{ marginTop: 6 }}>
                                {errors.pricingOptions.message}
                            </div>
                        )}
                    </div>
                    )}

                    {/* Description */}
                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                            <div className={styles.field}>
                                <label htmlFor="service-description">Description (optional)</label>
                                <textarea
                                    id="service-description"
                                    placeholder="What's included, what to expect, any prep notes…"
                                    value={field.value ?? ''}
                                    onChange={e => field.onChange(e.target.value)}
                                    disabled={loading}
                                    maxLength={1000}
                                />
                                {errors.description && (
                                    <div className={styles.fieldError}>{errors.description.message}</div>
                                )}
                            </div>
                        )}
                    />

                    {/* Settings card — active + home service */}
                    <div className={styles.settingsCard}>
                        <div className={styles.settingsCardTitle}>Service settings</div>
                        <div className={styles.settingsCardSubtitle}>
                            Visibility and where this service can be performed.
                        </div>

                        {!isAdd && (
                            <div className={styles.settingsRow}>
                                <div className={styles.settingsRowText}>
                                    <div className={styles.settingsRowLabel}>Active</div>
                                    <div className={styles.settingsRowHelp}>
                                        When on, customers can see and book this service. Turn off
                                        to hide it temporarily without deleting.
                                    </div>
                                </div>
                                <div className={styles.settingsRowControl}>
                                    <Controller
                                        name="isActive"
                                        control={control}
                                        render={({ field }) => (
                                            <Toggle
                                                checked={field.value}
                                                onChange={field.onChange}
                                                disabled={loading}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        )}

                        <div className={styles.settingsRow}>
                            <div className={styles.settingsRowText}>
                                <div className={styles.settingsRowLabel}>Home service available</div>
                                <div className={styles.settingsRowHelp}>
                                    Mark on if your staff can perform this service at the
                                    customer's home or office.
                                </div>
                            </div>
                            <div className={styles.settingsRowControl}>
                                <Controller
                                    name="hasHomeService"
                                    control={control}
                                    render={({ field }) => (
                                        <Toggle
                                            checked={field.value}
                                            onChange={field.onChange}
                                            disabled={loading}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className={styles.buttonContainer}>
                    <div className={styles.deleteSlot}>
                        {!isAdd && onDeleteClicked && (
                            <button
                                type="button"
                                className={`${styles.btn} ${styles.btnDanger}`}
                                onClick={onDeleteClicked}
                                disabled={loading}
                            >
                                Delete service
                            </button>
                        )}
                    </div>
                    <div className={styles.cancelAddSave}>
                        <button
                            type="button"
                            className={`${styles.btn} ${styles.btnGhost}`}
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={`${styles.btn} ${styles.btnPrimary}`}
                            onClick={handleSubmit(onValid)}
                            disabled={loading || !!durationConflict}
                        >
                            {isAdd ? 'Add' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServiceAddEditModal;
